import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { finalize, switchMap, takeUntil, of } from "rxjs";
import { Login } from "src/app/models/login";
import { AuthService } from "src/app/services/auth.service";
import { ErrorHandlerService } from "src/app/shared/error-handler.service";
import { BaseComponent } from "../../../shared/base.component";
import { jwtDecode } from "jwt-decode";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent extends BaseComponent implements OnInit {
  isLoading: boolean = false;
  hidePassword: boolean = true;
  loginForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private errorHandler: ErrorHandlerService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.loginForm = this.fb.group({
      username: [{ value: "", disabled: false }, [Validators.required]],
      password: [{ value: "", disabled: false }, Validators.required],
      rememberMe: [false],
    });
  }

  togglePasswordVisibility(): void {
    this.hidePassword = !this.hidePassword;
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.loginForm.disable();

    const { username, password, rememberMe } = this.loginForm.getRawValue();

    this.authService
      .login(username, password, rememberMe)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((token) => {
          if (rememberMe) {
            localStorage.setItem("authToken", token);
          } else {
            sessionStorage.setItem("authToken", token);
          }

          // Decodificar o token para verificar se é SUPER_ADMIN
          try {
            const decoded: any = jwtDecode(token);

            // Tentar diferentes formatos de roles que podem vir no token
            let userRoles: any[] = [];
            if (decoded.roles) {
              userRoles = Array.isArray(decoded.roles) ? decoded.roles : [];
            } else if (decoded.authorities) {
              userRoles = Array.isArray(decoded.authorities) ? decoded.authorities : [];
            } else if (decoded.role) {
              userRoles = Array.isArray(decoded.role) ? decoded.role : [decoded.role];
            }

            // Verificar se é SUPER_ADMIN (tentando diferentes formatos)
            const isSuperAdmin = userRoles.some((role: any) => {
              const roleName = typeof role === "string" ? role : role.name || role.authority || role;
              return roleName === "ROLE_SUPER_ADMIN" || roleName === "SUPER_ADMIN";
            });

            // Se for SUPER_ADMIN, não chamar /auth/me e construir o usuário a partir do token
            if (isSuperAdmin) {
              const superAdminUser = {
                id: decoded.sub || decoded.id || "",
                username: decoded.sub || decoded.username || username,
                email: decoded.email || "",
                name: decoded.name || decoded.username || username, // Adicionar campo name
                firstAccess: false,
                roles: userRoles.map((r: any) => (typeof r === "string" ? { name: r } : r)),
                office: "",
              };
              return of(superAdminUser);
            }
          } catch (error) {}

          // Para usuários normais, fazer a chamada para /auth/me
          return this.authService.getCurrentUser();
        }),
        finalize(() => {
          this.isLoading = false;
          this.loginForm.enable();
        }),
      )
      .subscribe({
        next: (user) => {
          this.safeExecute(() => {
            this.authService.setCurrentUser(user);

            // Verificar se é SUPER_ADMIN
            const userRoles = this.authService.getUserRoles();
            const isSuperAdmin = userRoles.some((role) => role.name === "ROLE_SUPER_ADMIN");

            if (isSuperAdmin) {
              this.router.navigate(["/super-admin"]).then((success) => {
                if (!success) {
                }
              });
              return;
            }

            if (user.firstAccess) {
              this.router.navigate(["primeiro-acesso"]);
              return;
            }

            this.router.navigate(["selecionar-empresa"], {
              state: { user: user },
            });
          });
        },
        error: (error) => {
          this.safeExecute(() => {
            this.errorHandler.handle(error, "Sistema está fora do ar no momento, tente mais tarde!");
          });
        },
        complete: () => {
          this.safeExecute(() => {
            // Só remover office se não for SUPER_ADMIN
            const userRoles = this.authService.getUserRoles();
            const isSuperAdmin = userRoles.some((role) => role.name === "ROLE_SUPER_ADMIN");
            if (!isSuperAdmin) {
              this.authService.removePropertyFromCurrentUser("office");
            }
          });
        },
      });
  }
}
